/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Input,
} from 'antd';
import { checkToken } from 'api/utils';
import Select from 'components/Select';
import EditableTable from 'components/EditableTable';
import { onError } from 'utils/handlers';
import {
  getLiquidacionesEmpleadosDescuentos,
  getLiquidacionEmpleadoDescuento,
  patchLiquidacionEmpleadoDescuento,
} from 'api/nominas/liquidaciones/liquidacionesEmpleadosDescuentos';
import { formatReceivedValues } from 'utils/formatValues';
import CurrencyInput from 'components/CurrencyInput';
import {
  FLOAT_NUMER_PATTERN,
} from 'utils/patterns';
import currencyFormatter from 'utils/currencyFormatter';
import { getConfiguracionDescuentos } from 'api/nominas/descuentos/configuracionDescuentos';
import { ESTATUS_AUTORIZADO } from 'utils/constants';

export const liquidacionEmpleadoDescuentoPermission = {
  permissionModel: 'liquidacionempleadodescuento',
  catalogsModels: [],
  tabsModels: [],
};

// eslint-disable-next-line react/prop-types
const LiquidacionesEmpleadosDescuentos = ({ permission, liquidacionEmpleado, onClickEditEncabezado }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [totalDescuentos, setTotalDescuentos] = useState(0);
  const [descuentos, setDescuentos] = useState();

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLiquidacionesEmpleadosDescuentos({ liquidacion_empleado: liquidacionEmpleado });
    setData(formatReceivedClone(_data));
    setTotalDescuentos(0);
    form.setFieldValue('importe_descuentos', 0);
    if (_data?.length) {
      let i = 0;
      let acum = 0;
      while (i < _data.length) {
        acum += _data[i].importe;
        i += 1;
      }
      setTotalDescuentos(acum);
      form.setFieldValue('importe_descuentos', acum);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _descuentos,
        _data,
      ] = await Promise.all([
        getConfiguracionDescuentos({ estados_globales: ESTATUS_AUTORIZADO }),
        await getLiquidacionesEmpleadosDescuentos({ liquidacion_empleado: liquidacionEmpleado }),
      ]);
      if (mounted) {
        setDescuentos(_descuentos);
        setData(_data);
        setTotalDescuentos(0);
        form.setFieldValue('importe_descuentos', 0);
        if (_data?.length) {
          let i = 0;
          let acum = 0;
          while (i < _data.length) {
            acum += _data[i].importe;
            i += 1;
          }
          setTotalDescuentos(acum);
          form.setFieldValue('importe_descuentos', acum);
        }
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _liquidacionEmpleadoIndemnizacion = await getLiquidacionEmpleadoDescuento(match.id);
    setTimeout(() => {
      if (_liquidacionEmpleadoIndemnizacion) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceivedClone(_liquidacionEmpleadoIndemnizacion));
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        liquidacion_empleado: liquidacionEmpleado,
      };
      const [key] = selectedRowKeys;
      const saved = await patchLiquidacionEmpleadoDescuento(key, values);
      if (saved) {
        if (_continue) {
          await fetchData();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
          await onClickEditEncabezado();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
      );
    }
  };

  const handleOnRowClick = async (record) => {
    await form.validateFields();
    if (selectedRowKeys.length) {
      if (selectedRowKeys[0] === record.id) {
        return;
      }
      await onFinish();
    }
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
    valor: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
  };

  const columns = [
    {
      titleText: 'Concepto',
      dataIndex: 'descuento',
      key: 'descuento',
      width: 300,
      render: (val, record) => {
        if (selectedRowKeys[0] === record.id) {
          return (
            <Form.Item
              name="descuento"
              label="Descuento"
              rules={rules.required}
              hasFeedback
            >
              <Select
                disabled
                dataSource={descuentos}
                render={(e) => e?.concepto}
              />
            </Form.Item>
          );
        }
        return <p>{descuentos.find((perc) => perc.id === val)?.concepto}</p>;
      },
    },
    {
      titleText: 'Referencia',
      dataIndex: 'referencia',
      key: 'referencia',
      width: 300,
      render: (val, record) => {
        if (selectedRowKeys[0] === record.id) {
          return (
            <Form.Item
              name="referencia"
              label="Referencia"
              hasFeedback
            >
              <Input />
            </Form.Item>
          );
        }
        return <p>{val}</p>;
      },
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 200,
      align: 'right',
      render: (val, record) => {
        if (selectedRowKeys[0] === record.id) {
          return (
            <CurrencyInput
              name="importe"
              label="Importe"
              rules={rules.valor}
              hasFeedback
            />
          );
        }
        return <p>{currencyFormatter(val || 0)}</p>;
      },
    },
  ];

  const summaryCells = [
    {
      label: 'Total',
      value: totalDescuentos,
      valueAlign: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          name="general"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onFinish();
            }
          }}
        >
          <EditableTable
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            allowSearch={false}
            controls={{
              onClickFinish: () => onFinish(),
              onClickCancel: () => onCancel(),
            }}
            summaryCells={summaryCells}
            summaryColumnPosition={3}
            permission={permission}
          />
          <Col xs={24} sm={24} md={5}>
            <CurrencyInput
              name="importe_descuentos"
              label="Importe de Descuentos"
              disabled
            />
          </Col>
        </Form>
      </Spin>
    </Row>
  );
};

export default LiquidacionesEmpleadosDescuentos;
